/**
 * KesihatanTab - Health tab for daily log form (medicine, injuries)
 */
import { Camera, Clock, Pill } from "lucide-react";

const HOURS = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
const MINUTES = ['00', '15', '30', '45'];

const UBAT_LIST = [
  { id: 'demam', label: 'Demam' },
  { id: 'batuk', label: 'Batuk' },
  { id: 'selsema', label: 'Selsema' },
  { id: 'antibiotik', label: 'Antibiotik' },
  { id: 'ubat_titik', label: 'Ubat Titik' },
  { id: 'ubat_sapu', label: 'Ubat Sapu' }
];

export default function KesihatanTab({ formData, updateFormField, setHealthPhotoFile }) {
  return (
    <div className="space-y-4 p-3 bg-red-50 rounded-lg">
      {/* Ubat Diberi */}
      <div>
        <label className="block text-sm font-medium text-red-800 mb-2">
          <Pill className="w-4 h-4 inline mr-1" /> Ubat Diberi
        </label>
        <p className="text-xs text-gray-500 mb-2">Pilih jenis ubat dan masukkan masa diberi</p>
        <div className="space-y-2">
          {UBAT_LIST.map(ubat => (
            <div key={ubat.id} className="bg-white p-3 rounded-lg border border-red-200">
              <label className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={formData.ubat_diberi?.[ubat.id]?.selected || false}
                  onChange={(e) => updateFormField(`ubat_diberi.${ubat.id}.selected`, e.target.checked)}
                  className="rounded border-red-300 text-red-500 focus:ring-red-500"
                />
                <span className="text-sm font-medium">{ubat.label}</span>
              </label>
              {formData.ubat_diberi?.[ubat.id]?.selected && (
                <div className="ml-6 flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-gray-500">Jam diberi:</span>
                  <select
                    value={formData.ubat_diberi?.[ubat.id]?.hour || ''}
                    onChange={(e) => updateFormField(`ubat_diberi.${ubat.id}.hour`, e.target.value)}
                    className="px-2 py-1 border border-red-200 rounded text-sm"
                  >
                    <option value="">--</option>
                    {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <span>:</span>
                  <select
                    value={formData.ubat_diberi?.[ubat.id]?.minute || '00'}
                    onChange={(e) => updateFormField(`ubat_diberi.${ubat.id}.minute`, e.target.value)}
                    className="px-2 py-1 border border-red-200 rounded text-sm"
                  >
                    {MINUTES.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select
                    value={formData.ubat_diberi?.[ubat.id]?.period || 'AM'}
                    onChange={(e) => updateFormField(`ubat_diberi.${ubat.id}.period`, e.target.value)}
                    className="px-2 py-1 border border-red-200 rounded text-sm"
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Nota tambahan..."
                    value={formData.ubat_diberi?.[ubat.id]?.notes || ''}
                    onChange={(e) => updateFormField(`ubat_diberi.${ubat.id}.notes`, e.target.value)}
                    className="flex-1 min-w-[120px] px-2 py-1 border border-red-200 rounded text-sm"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Kecederaan/Injury Section */}
      <div className="border-t border-red-200 pt-4">
        <label className="block text-sm font-bold text-red-800 mb-2">
          Kecederaan / Penyakit (dari rumah)
        </label>
        <div className="bg-white rounded-lg border border-red-200 p-3 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Jenis Kecederaan / Penyakit</label>
            <input
              type="text"
              value={formData.injury?.description || ''}
              onChange={(e) => updateFormField('injury.description', e.target.value)}
              placeholder="Contoh: Luka calar di lutut, demam ringan..."
              className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm"
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Lokasi Kecederaan (Bahagian Badan)</label>
            <select
              value={formData.injury?.location || ''}
              onChange={(e) => updateFormField('injury.location', e.target.value)}
              className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm"
            >
              <option value="">-- Pilih lokasi --</option>
              <option value="Kepala">Kepala</option>
              <option value="Muka">Muka</option>
              <option value="Tangan Kiri">Tangan Kiri</option>
              <option value="Tangan Kanan">Tangan Kanan</option>
              <option value="Kaki Kiri">Kaki Kiri</option>
              <option value="Kaki Kanan">Kaki Kanan</option>
              <option value="Badan">Badan</option>
              <option value="Belakang">Belakang</option>
              <option value="Lain-lain">Lain-lain</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Waktu Dikesan</label>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-400" />
              <input
                type="time"
                value={formData.injury?.time_detected || ''}
                onChange={(e) => updateFormField('injury.time_detected', e.target.value)}
                className="px-3 py-2 border border-red-200 rounded-lg text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Punca Berlaku</label>
            <select
              value={formData.injury?.origin || ''}
              onChange={(e) => updateFormField('injury.origin', e.target.value)}
              className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm"
            >
              <option value="">-- Pilih --</option>
              <option value="Dari rumah">Dari rumah</option>
              <option value="Di taska">Di taska</option>
              <option value="Tidak pasti">Tidak pasti</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Nota Tambahan</label>
            <textarea
              value={formData.injury?.notes || ''}
              onChange={(e) => updateFormField('injury.notes', e.target.value)}
              rows={2}
              placeholder="Sebarang catatan tambahan..."
              className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm"
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              <Camera className="w-4 h-4 inline mr-1" /> Gambar Saringan (Gejala)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setHealthPhotoFile(e.target.files[0])}
              className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm bg-white"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
